import React, { useCallback, useEffect, useRef, useState } from 'react';

import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';

import { FiscalCodeOption } from '../../services/fiscalCatalogService';

import { Container } from './styles';

interface FiscalCodeAutocompleteProps {
  id: string;
  name?: string;
  label: string;
  placeholder?: string;
  value: string;
  onChange: (codigo: string) => void;
  /** Busca remota; recebe o texto digitado. */
  loadOptions: (termo: string) => Promise<FiscalCodeOption[]>;
  /**
   * Recarrega a lista quando muda (ex.: o CEST depende do NCM escolhido). Serve
   * só como gatilho — quem usa o valor é o `loadOptions` de quem chama.
   */
  reloadKey?: string;
  /** Busca já ao abrir, sem esperar digitação (usado com `reloadKey`). */
  loadOnOpen?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
}

const DEBOUNCE_MS = 350;

/**
 * Campo de código fiscal (NCM/CEST) com busca em catálogo.
 *
 * `freeSolo` de propósito: o catálogo não pode virar uma trava. Código novo que
 * a tabela ainda não tem, ou catálogo fora do ar, continuam digitáveis — a
 * validação de formato de quem usa é que decide o que é aceitável.
 */
const FiscalCodeAutocomplete: React.FC<FiscalCodeAutocompleteProps> = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  loadOptions,
  reloadKey,
  loadOnOpen = false,
  disabled = false,
  error,
  helperText,
}) => {
  const [options, setOptions] = useState<FiscalCodeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [aberto, setAberto] = useState(false);

  // Guarda a busca em voo para descartar resposta atrasada que chegaria depois
  // de uma digitação mais nova (senão a lista "volta" para o termo anterior).
  const buscaAtual = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // `loadOptions` costuma vir como arrow inline (ex.: o CEST fecha o NCM atual
  // por closure), ou seja, referência NOVA a cada render. Guardá-la num ref
  // mantém `buscar` estável: se ela entrasse nas dependências, o efeito de
  // `loadOnOpen` abaixo rodaria a cada render — busca → setState → render →
  // busca — e a lista ficava piscando sem nunca assentar.
  const loadOptionsRef = useRef(loadOptions);
  useEffect(() => {
    loadOptionsRef.current = loadOptions;
  });

  const buscar = useCallback((termo: string) => {
    const meuId = buscaAtual.current + 1;
    buscaAtual.current = meuId;

    setLoading(true);
    loadOptionsRef
      .current(termo)
      .then((resultado) => {
        if (buscaAtual.current !== meuId) return;
        setOptions(resultado);
      })
      .finally(() => {
        if (buscaAtual.current === meuId) setLoading(false);
      });
  }, []);

  const buscarComDebounce = useCallback(
    (termo: string) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => buscar(termo), DEBOUNCE_MS);
    },
    [buscar],
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  // Recarrega quando o campo do qual este depende muda (CEST ← NCM), mas só com
  // a lista aberta — evita uma chamada por tecla digitada no campo de cima.
  //
  // Busca com termo VAZIO de propósito: ao abrir, o usuário quer ver as opções
  // do contexto (todos os CEST daquele NCM, todas as classificações daquele
  // CST). Buscar pelo valor já preenchido devolveria uma lista de um item só.
  useEffect(() => {
    if (loadOnOpen && aberto) buscar('');
  }, [reloadKey, aberto, loadOnOpen, buscar]);

  return (
    <Container>
      <label htmlFor={id}>{label}</label>
      <Autocomplete
        id={id}
        freeSolo
        disabled={disabled}
        open={aberto}
        onOpen={() => setAberto(true)}
        onClose={() => setAberto(false)}
        options={options}
        loading={loading}
        // `value` E `inputValue` controlados: só com `inputValue`, o MUI
        // considera que nada está selecionado e ressincroniza o texto para o
        // label do `value` (vazio), apagando o campo.
        value={value}
        inputValue={value}
        filterOptions={(x) => x} // a filtragem é do servidor, não local
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.codigo
        }
        getOptionSelected={(option, selecionado) =>
          option.codigo ===
          (typeof selecionado === 'string' ? selecionado : selecionado?.codigo)
        }
        onChange={(_event, selecionado) =>
          onChange(
            typeof selecionado === 'string'
              ? selecionado
              : selecionado?.codigo || '',
          )
        }
        renderOption={(option: FiscalCodeOption) => (
          <div className="option">
            <strong>{option.codigo}</strong>
            <span>{option.descricao}</span>
            {option.detalhe && <small>{option.detalhe}</small>}
          </div>
        )}
        onInputChange={(_event, novoValor, reason) => {
          // 'reset' é o MUI ressincronizando o texto com o `value` (acontece ao
          // montar e ao fechar a lista) — NÃO é edição do usuário. Repassar
          // isso apagava o código já gravado assim que a tela abria.
          if (reason === 'reset') return;

          onChange(novoValor);
          if (reason === 'input') buscarComDebounce(novoValor);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            placeholder={placeholder}
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error ? (
        <span className="field-error">{error}</span>
      ) : (
        helperText && <span className="field-help">{helperText}</span>
      )}
    </Container>
  );
};

export default FiscalCodeAutocomplete;
